import { useEffect, useRef } from 'react';
import type { AssistantToolInfo } from '@cumpli/shared';
import { cn } from '../ui';
import { toolIcon } from './tool-icons';

/**
 * The "/" quick-invoke menu shown ABOVE the composer whenever the input starts
 * with "/". It lists catalog items whose label/name match the text typed after
 * the slash and, unlike the empty-state explore cards, works REGARDLESS of
 * whether a conversation exists — that's the whole point of "/".
 *
 * Presentational only: the composer/page owns the filtered `items`, the active
 * row index, and the keyboard handling (↑/↓/Enter/Esc live on the input so there
 * is a single source of truth). Mirrors the command-palette row idiom: a lucide
 * icon + label + a right-aligned mono category tag.
 */

/** Strip accents + lowercase so "revision" matches "revisión". */
const DIACRITICS = /\p{Diacritic}/gu;
function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(DIACRITICS, '');
}

/** es-PA category tag shown at the right of each row. */
export function categoryTag(category: AssistantToolInfo['category']): string {
  return category === 'accion' ? 'Acción' : 'Consulta';
}

/**
 * Filter the catalog by the text typed after "/". Empty query → the whole
 * catalog (so bare "/" reveals everything). Matches on the display label or the
 * raw tool name, accent-insensitively.
 */
export function filterTools(
  tools: readonly AssistantToolInfo[],
  query: string
): AssistantToolInfo[] {
  const q = normalize(query.trim());
  if (!q) return [...tools];
  return tools.filter((t) => normalize(t.label).includes(q) || t.name.includes(q));
}

export function SlashMenu({
  items,
  activeIndex,
  onSelect,
  onActiveChange,
}: {
  items: readonly AssistantToolInfo[];
  activeIndex: number;
  /** Seed the composer with the picked tool's prompt (no auto-send). */
  onSelect: (tool: AssistantToolInfo) => void;
  /** Mouse hover moves the active row so keyboard + pointer stay in sync. */
  onActiveChange: (index: number) => void;
}) {
  // Keyboard nav lives on the composer input (single source of truth); it only
  // updates `activeIndex`. This menu is `max-h-72 overflow-y-auto` and opens
  // UPWARD (`bottom-full`), so once ↑/↓ moves the highlight past the visible
  // window the active row would look stuck. Pull the active <button> back into
  // view on every `activeIndex` change — `block: 'nearest'` scrolls the minimum
  // needed, so both directions and the upward orientation stay on screen.
  const activeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  return (
    <div
      role="listbox"
      aria-label="Comandos del agente"
      // Positioned above the composer; the parent wraps the composer in a
      // `relative` box. `max-h` + scroll keeps it inside the narrow drawer.
      onMouseDown={(e) => e.preventDefault()} // keep the composer input focused
      className="absolute inset-x-0 bottom-full z-20 mb-2 max-h-72 overflow-y-auto rounded-2xl border border-line bg-panel p-1.5 shadow-pop"
    >
      <div className="px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-faint">
        Comandos
      </div>
      {items.map((tool, i) => {
        const Icon = toolIcon(tool.name);
        const active = i === activeIndex;
        return (
          <button
            key={tool.name}
            ref={active ? activeRef : null}
            type="button"
            role="option"
            aria-selected={active}
            onMouseEnter={() => onActiveChange(i)}
            onClick={() => onSelect(tool)}
            className={cn(
              'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-ink transition-colors',
              active ? 'bg-canvas' : 'hover:bg-canvas'
            )}
          >
            <Icon className="h-[18px] w-[18px] shrink-0 text-primary/80" strokeWidth={1.75} />
            <span className="min-w-0 flex-1 truncate">{tool.label}</span>
            <span className="ml-auto shrink-0 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
              {categoryTag(tool.category)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
